import { useEffect, useState } from 'react';
import logger from '@gral/log';

import FormContainer from '@pages/DataBase/Flavors/FormContainer';
import Error404 from '@components/Errors/404';

import { Loading } from '@components/Misc';
import App from '@gral/app';
import { useSetState } from 'react-use';
import { useAppDispatch, useAppSelector, useObjectSelector } from '@redux/store';
import { useRouterContext } from '@utils/context/RouterContext';
import {
  deleteData,
  fetchData,
  patchData,
  postData,
  putData,
  updateData,
} from '@redux/slices/functionals/request/requestActions';
import { TMode, IConfigComponent, IEntity, CEntity, EAclAction, newCClassEntity } from '@ts/ui-models';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import { Button, Modal, GridContainerFullHeight, TTextType, TNormalizer } from '@ts/storybook';
import WithAllPropsApp from '@utils/wrappers/WithAllPropsApp';
import { deleteEntity } from '@services/ApiService';
import { useLocation } from 'react-router';

interface CrudPageProps {
  configComponent: IConfigComponent;
  defaultValues?: any;
  extraActionToDoAfterCorrectSubmit?: (values, options: { itemId: string; responseData: any }) => Promise<void>;
  isNeededConfirmation?: boolean;
  onCancel?: () => void;
  form: any; //Component to be rendered
  validationSchema: any; //YUP validation
  zodValidationSchema: any; //ZOD validation
  normalize?: TNormalizer<any>;
  essentialValues?: Set<string>; //keys that are required
  warningToDelete?: TTextType;
  methodCRUD?: 'put' | 'patch';
}
const log = logger('CRUDPage');
const CrudPage = (props: CrudPageProps) => {
  const { navigate, mode, tEntity: tEntityURL, transferPropsRoute } = useRouterContext();
  const isNewMode = mode === 'new';
  const dispatch = useAppDispatch();
  const location = useLocation();
  const locationState = location.state ?? {};
  const { defaultValues = {}, essentialValues = new Set(), isNeededConfirmation = true, methodCRUD = 'patch' } = props;
  const historyRoutes = useAppSelector(state => state.router.historyRoutes);
  const { data: iEntityData, loading = true, error } = useObjectSelector(state => state.request['crud_item']);
  const [cEntityData, setCEntityData] = useState<CEntity<IEntity> | null>(null);
  const tEntityID: string = transferPropsRoute.params.id ?? cEntityData?.getID();
  const crudApiURL = props.configComponent?.crudURL ?? props.configComponent?.fetchURL ?? `api/${tEntityURL}`;
  const crudClass = props.configComponent?.crudClass ?? props.configComponent?.mainClass;

  const [state, setState] = useSetState<{
    valuesToProcess: any;
    responseData: CEntity<IEntity> | null;
    isSubmiting: boolean;
    showDeleteConfirmation: boolean;
  }>({
    valuesToProcess: {},
    responseData: null,
    isSubmiting: false,
    showDeleteConfirmation: false,
  });

  //#region Handlers
  const handleClickNew = () => {
    // goto item/new
    let url = `/${cEntityData?.tEntity}/new`;
    navigate(url, { state: { title: 'New ' + cEntityData?.getLabelEntity() } });
  };

  const handleClickEdit = () => {
    let url = `/${tEntityURL}/${tEntityID}/edit`;
    const titleNewRoute = `${cEntityData?.getLabelEntity()} | ${cEntityData?.toString()}`;
    navigate(url, { state: { title: titleNewRoute } });
  };

  const handleClickDelete = () => {
    if (isNeededConfirmation) setState({ showDeleteConfirmation: true });
    else handleConfirmDelete();
  };

  const handleConfirmDelete = async () => {
    const deleteSuccess = (await deleteEntity(dispatch, props.configComponent, cEntityData?.getID())).error;
    if (!deleteSuccess) {
      navigate(-1);
    }
  };

  const handleClickDoneOrSave = () => {
    const route = historyRoutes[historyRoutes.length - 2];
    if (route) {
      navigate(route?.fullPath ?? route?.matchPath ?? '/');
    } else {
      let url = `/${cEntityData?.tEntity}/${state.responseData?.getID() || tEntityID}/view`;
      const titleNewRoute = `${cEntityData?.getLabelEntity()} | ${(state.responseData || cEntityData)?.toString()}`;
      navigate(url, { state: { title: titleNewRoute } });
    }
  };

  const handleFormSubmit = async attrs => {
    const { operation, url } = getRequestData();
    log.info('handleFormSubmit', operation, url, attrs);

    attrs = processAttrsBeforeSend(url, attrs);

    let responseData: any = null;

    switch (operation) {
      case 'create':
        if (props.configComponent.postEntity) {
          responseData = (await props.configComponent.postEntity(dispatch, attrs)).payload?.data;
        } else {
          responseData = (await dispatch(postData({ url, dataBody: attrs }))).payload?.data;
        }
        break;
      case 'patch':
        responseData = (await dispatch(patchData({ url, dataBody: attrs }))).payload?.data;
        break;
      case 'put':
        responseData = (await dispatch(putData({ url, dataBody: attrs }))).payload?.data;
        break;
      case 'update': //v1 - v2
        responseData = (await dispatch(updateData({ url, dataBody: attrs }))).payload?.data;
        break;
      default:
        log.error('handleFormSubmit: unsupported operation', operation);
        throw new Error(`Unsupported operation: ${operation}`);
    }

    const entityData = responseData?.data ?? responseData;

    if (entityData) {
      setState({
        valuesToProcess: attrs,
        responseData: newCClassEntity(entityData, crudClass),
        isSubmiting: true,
      });
    }
  };

  const handleIsSubmittingUpdate = () => {
    const curDataStoreState: any = state.responseData;

    if (!curDataStoreState) {
      // abort sumbit
      setState({ isSubmiting: false });
    } else {
      let itemId = curDataStoreState?.logicalId;
      setCEntityData(newCClassEntity(crudApiURL.includes('v3') ? curDataStoreState.data : curDataStoreState, crudClass));
      if (props.extraActionToDoAfterCorrectSubmit) {
        props
          .extraActionToDoAfterCorrectSubmit(state.valuesToProcess, {
            itemId,
            responseData: state.responseData,
          })
          .then(v => {
            handleClickDoneOrSave();
          })
          .catch(e => {
            setState({ isSubmiting: false });
          });
      } else {
        handleClickDoneOrSave();
      }
    }
  };
  //#endregion

  //#region Functions
  const isCurrentMode = (tMode: TMode) => {
    return mode === tMode;
  };
  const checkAclPermision = type => App.cxStore.checkPermission(props.configComponent.aclId, type);

  const fetchItem = () => {
    const url = `${crudApiURL}/${tEntityID}`;
    log.info('1 - Fetch item url:', url);
    dispatch(fetchData({ key: 'crud_item', url, dataBody: {} }));
  };
  const getRequestData = (): { url: string; operation: 'create' | 'put' | 'patch' | 'update' } => {
    switch (mode) {
      case 'new':
        return {
          url: crudApiURL.includes('v3') ? crudApiURL : `${crudApiURL}/new`,
          operation: 'create',
        };
      case 'edit':
        return {
          url: `${crudApiURL}/${tEntityID}`,
          operation: crudApiURL.includes('v3') ? methodCRUD : 'update',
        };
      default:
        throw Error(`Called submit in not supported mode ${mode}`);
    }
  };

  const processAttrsBeforeSend = (url: string, attrs0: any) => {
    //if (url.includes('v3') && attrs0.tEntity) delete attrs0.tEntity;
    if (methodCRUD === 'put') delete attrs0.id;
    delete attrs0.tEntity;
    // For those fields where we need to do something before sending
    // like trimming spaces for names or customIds
    const attrs = { ...attrs0 };
    if (attrs.name) {
      attrs.name = attrs.name.trim();
    }
    if (attrs.logicalId) {
      attrs.logicalId = attrs.logicalId.trim();
    }
    if (attrs.customId) {
      attrs.customId = attrs.customId.trim();
    }
    return attrs;
  };
  //#endregion

  //#region Renders
  const renderActionButtons = () => {
    // Navigational buttons (only in read mode)
    if (!isCurrentMode('view')) return;
    return (
      <>
        <Button label="Done" id={`crudDone`} onClick={handleClickDoneOrSave} variant="text" size="large" />
        <Button
          id={`crudEdit`}
          label="Edit"
          disabled={!checkAclPermision(EAclAction.EDIT)}
          variant="text"
          onClick={handleClickEdit}
          size="large"
        />
        <Button
          id={`crudDelete`}
          label="Delete"
          disabled={!checkAclPermision(EAclAction.DECOMMISSION)}
          variant="text"
          onClick={handleClickDelete}
          size="large"
        />
        <Button
          id={`crudNew`}
          label="New"
          disabled={!checkAclPermision(EAclAction.CREATE)}
          variant="text"
          onClick={handleClickNew}
          size="large"
        />
      </>
    );
  };

  const renderDeleteConfirmation = () => {
    return (
      <Modal
        withHeader
        open={state.showDeleteConfirmation}
        title={['You are about to delete ', tEntityURL, '. ', 'Are you sure', '?']}
        warningToShow={props.warningToDelete}
        onSubmit={handleConfirmDelete}
        labelOk="Delete"
        labelCancel="Cancel"
        onClose={() => setState({ showDeleteConfirmation: false })}
        fullWidth
        maxWidth="xs"
      />
    );
  };
  //#endregion

  /*   useEffectAfterMount(() => {
    if (!isNewMode && tEntityURL) {
      fetchItem();
    }
  }, [tEntityURL]); */

  useEffectAfterMount(() => {
    if (!iEntityData) return;
    log.info(`2 - ${mode} Entity`, iEntityData);

    setCEntityData(
      newCClassEntity(isNewMode ? props.defaultValues : crudApiURL.includes('v3') ? iEntityData.data : iEntityData, crudClass)
    );
  }, [iEntityData]);

  useEffect(() => {
    if (state.isSubmiting) {
      handleIsSubmittingUpdate();
    }
  }, [state.isSubmiting]);

  useEffect(() => {
    log.info('0 - Change mode: ', mode);
    log.info('0.1 - Navigate state: ', locationState);
    if (isCurrentMode('new')) {
      setCEntityData(newCClassEntity({ ...defaultValues, ...locationState?.entity }, crudClass));
    }
    if (isCurrentMode('view')) {
      fetchItem();
      setState({ isSubmiting: false });
    }
    if (isCurrentMode('edit')) {
      if (!cEntityData) fetchItem();
    }
  }, [mode]);

  if (!cEntityData && !error && !loading) return;
  else if (loading && !isNewMode) return <Loading />;
  else if (error && !isNewMode) return <Error404 message={['Entity with ID', ' ', tEntityID, ' ', 'not found', '.']} />;

  return (
    <GridContainerFullHeight id="crud-container">
      {renderDeleteConfirmation()}
      <FormContainer
        {...props}
        initialValues={cEntityData}
        essentialValues={essentialValues}
        form={props.form}
        onSubmit={handleFormSubmit}
        onCancel={props.onCancel ? props.onCancel : () => window.history.back()}
        renderActionButtons={renderActionButtons}
        isSubmiting={state.isSubmiting}
        validationSchema={props.validationSchema}
        zodValidationSchema={props.zodValidationSchema}
      />
    </GridContainerFullHeight>
  );
};

//We need to remove this wrapper when we remove old components/flux
export default WithAllPropsApp(CrudPage);
